// src/lib/analytics.js
// Single home for every tracking tag on the site: Google Analytics 4,
// Klaviyo onsite tracking and the Meta Pixel. Nothing else in the app should
// load a tracking script or call gtag/fbq/klaviyo directly — components call
// the track* helpers below and this module decides what is allowed to run.
//
// Consent model (Google Consent Mode v2, "basic" implementation):
//   - Consent defaults are set to "denied" before any Google tag runs.
//   - gtag.js is only downloaded once the visitor allows Analytics cookies.
//   - Klaviyo and Meta are only loaded once the visitor allows Marketing cookies.
//   - The choice is also passed to Shopify, so checkout respects it and does
//     not ask the visitor a second time.
//
// Meta Pixel ID is public (it appears in every page's code), so it lives here.
// REACT_APP_META_PIXEL_ID in Netlify overrides it; set that to 'off' to disable.

import { startLiveTracking, stopLiveTracking, livePageView, liveEvent, liveSessionId } from './liveTracker';

const GA_ID = 'G-GX1QSPGHPT';
const KLAVIYO_COMPANY_ID = 'VAf7Tx';
const META_PIXEL_ENV = process.env.REACT_APP_META_PIXEL_ID;
const META_PIXEL_ID = META_PIXEL_ENV === 'off' ? '' : (META_PIXEL_ENV || '1257011749935831');

const BRAND = "Maison D'Oud";
const SITE_ORIGIN = 'https://www.maisondoud.com';
const CHECKOUT_ROOT_DOMAIN = 'shop.maisondoud.com';
const STOREFRONT_ROOT_DOMAIN = 'maisondoud.com';

const CONSENT_KEY = 'cookie_consent';
const CAMPAIGN_SESSION_KEY = 'md_campaign';
const CAMPAIGN_STORED_KEY = 'md_campaign_30d';
const CAMPAIGN_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const QUIZ_RESULT_KEY = 'md_quiz_result';
const CAMPAIGN_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid',
];

export const CONSENT_CHANGED_EVENT = 'md:consent-changed';
export const OPEN_COOKIE_PREFERENCES_EVENT = 'md:open-cookie-preferences';

let initialised = false;
let gaLoaded = false;
let klaviyoLoaded = false;
let metaLoaded = false;

// ─── Small helpers ──────────────────────────────────────────────────────────

function safeStorage(type) {
    try {
        return window[type];
    } catch {
        return null;
    }
}

function readJSON(storage, key) {
    try {
        const raw = storage?.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeJSON(storage, key, value) {
    try {
        storage?.setItem(key, JSON.stringify(value));
    } catch {
        // Storage full or blocked — tracking is best-effort, never break the site
    }
}

function loadScript(src, id) {
    return new Promise((resolve, reject) => {
        const existing = document.getElementById(id);
        if (existing) {
            if (existing.dataset.loaded === 'true') resolve();
            else {
                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', reject);
            }
            return;
        }
        const script = document.createElement('script');
        script.id = id;
        script.async = true;
        script.src = src;
        script.addEventListener('load', () => {
            script.dataset.loaded = 'true';
            resolve();
        });
        script.addEventListener('error', reject);
        document.head.appendChild(script);
    });
}

// "gid://shopify/ProductVariant/123" → "123"
export function shopifyNumericId(gid) {
    if (!gid) return '';
    const parts = String(gid).split('/');
    return parts[parts.length - 1];
}

function eventId(prefix) {
    return `${prefix}.${Date.now()}.${Math.random().toString(36).slice(2, 10)}`;
}

function productUrl(handle) {
    return handle ? `${SITE_ORIGIN}/product/${handle}` : SITE_ORIGIN;
}

// Runs one vendor's call so an error in one tool (e.g. Klaviyo) can never
// stop the others (e.g. Meta) from receiving the same event.
function safely(label, fn) {
    try {
        fn();
    } catch (err) {
        if (typeof console !== 'undefined') console.warn(`[analytics] ${label} failed:`, err);
    }
}

// ─── Consent ────────────────────────────────────────────────────────────────

// Returns { essential, analytics, marketing } or null if the visitor hasn't chosen yet.
export function getConsent() {
    const storage = safeStorage('localStorage');
    let raw = null;
    try {
        raw = storage?.getItem(CONSENT_KEY);
    } catch {
        raw = null;
    }
    if (!raw) return null;
    // Legacy string format from the first version of the banner
    if (raw === 'accepted') return { essential: true, analytics: true, marketing: true };
    if (raw === 'declined') return { essential: true, analytics: false, marketing: false };
    try {
        const parsed = JSON.parse(raw);
        return {
            essential: true,
            analytics: !!parsed.analytics,
            marketing: !!parsed.marketing,
        };
    } catch {
        return null;
    }
}

function hasConsent(type) {
    const consent = getConsent();
    return !!(consent && consent[type]);
}

// Called by the cookie banner when the visitor makes or changes a choice.
export function saveConsent(prefs) {
    const consent = {
        essential: true,
        analytics: !!prefs.analytics,
        marketing: !!prefs.marketing,
    };
    writeJSON(safeStorage('localStorage'), CONSENT_KEY, consent);
    applyConsent(consent);
    syncShopifyConsent(consent);
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: consent }));
}

export function openCookiePreferences() {
    window.dispatchEvent(new CustomEvent(OPEN_COOKIE_PREFERENCES_EVENT));
}

function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
}

function applyConsent(consent) {
    const analytics = consent.analytics ? 'granted' : 'denied';
    const marketing = consent.marketing ? 'granted' : 'denied';

    gtag('consent', 'update', {
        analytics_storage: analytics,
        ad_storage: marketing,
        ad_user_data: marketing,
        ad_personalization: marketing,
    });

    if (consent.analytics) loadGoogleAnalytics();

    // Our own first-party analytics (src/lib/liveTracker.js). Off unless
    // REACT_APP_LIVE_ANALYTICS is "on"; runs only with Analytics consent.
    safely('Live analytics', () => {
        if (consent.analytics) startLiveTracking({ getCampaign });
        else stopLiveTracking();
    });

    if (consent.marketing) {
        loadKlaviyo();
        loadMetaPixel();
        if (metaLoaded) window.fbq('consent', 'grant');
    } else if (metaLoaded) {
        window.fbq('consent', 'revoke');
    }

    // Persist the campaign that brought this visitor in, now that we're allowed to.
    if (consent.analytics || consent.marketing) {
        const campaign = readJSON(safeStorage('sessionStorage'), CAMPAIGN_SESSION_KEY);
        if (campaign) writeJSON(safeStorage('localStorage'), CAMPAIGN_STORED_KEY, campaign);
    } else {
        try {
            safeStorage('localStorage')?.removeItem(CAMPAIGN_STORED_KEY);
            safeStorage('localStorage')?.removeItem(QUIZ_RESULT_KEY);
        } catch {
            // ignore
        }
    }
}

// Shopify's Customer Privacy API. Writes the visitor's choice to a cookie on
// maisondoud.com, which checkout at shop.maisondoud.com reads — so Shopify's own
// banner doesn't ask again and the Google/Meta apps on checkout obey the choice.
function syncShopifyConsent(consent) {
    const token = process.env.REACT_APP_SHOPIFY_TOKEN;
    if (!token) return;
    loadScript(
        'https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.1/consent-tracking-api.js',
        'shopify-consent-api'
    )
        .then(() => {
            const privacy = window.Shopify?.customerPrivacy;
            if (!privacy?.setTrackingConsent) return;
            privacy.setTrackingConsent(
                {
                    analytics: consent.analytics,
                    marketing: consent.marketing,
                    preferences: consent.analytics,
                    sale_of_data: consent.marketing,
                    headlessStorefront: true,
                    checkoutRootDomain: CHECKOUT_ROOT_DOMAIN,
                    storefrontRootDomain: STOREFRONT_ROOT_DOMAIN,
                    storefrontAccessToken: token,
                },
                () => {}
            );
        })
        .catch(() => {
            // Shopify's script blocked or offline — checkout will show its own banner instead
        });
}

// ─── Tag loaders ────────────────────────────────────────────────────────────

function loadGoogleAnalytics() {
    if (gaLoaded) return;
    gaLoaded = true;
    // Queued only now, after the consent update, so the first page_view is
    // processed with analytics_storage already granted.
    gtag('js', new Date());
    gtag('config', GA_ID);
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`, 'ga-script').catch(() => {
        gaLoaded = false;
    });
}

function klaviyoQueue() {
    window.klaviyo = window.klaviyo || [];
    return window.klaviyo;
}

function loadKlaviyo() {
    if (klaviyoLoaded) return;
    klaviyoLoaded = true;
    klaviyoQueue();
    loadScript(
        `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${KLAVIYO_COMPANY_ID}`,
        'klaviyo-script'
    ).catch(() => {
        klaviyoLoaded = false;
    });
}

function loadMetaPixel() {
    if (metaLoaded || !META_PIXEL_ID) return;
    metaLoaded = true;
    /* eslint-disable */
    // Meta's standard base code, unminified enough to read
    if (!window.fbq) {
        const n = (window.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        if (!window._fbq) window._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
    }
    /* eslint-enable */
    loadScript('https://connect.facebook.net/en_US/fbevents.js', 'meta-pixel-script').catch(() => {});
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
}

// ─── Campaign (UTM / click ID) capture ──────────────────────────────────────

function captureCampaign() {
    let params;
    try {
        params = new URLSearchParams(window.location.search);
    } catch {
        return;
    }
    const found = {};
    CAMPAIGN_PARAMS.forEach((key) => {
        const value = params.get(key);
        if (value) found[key] = value.slice(0, 200);
    });

    const referrer = document.referrer || '';
    let externalReferrer = '';
    try {
        const host = referrer ? new URL(referrer).hostname : '';
        if (host && !host.endsWith(STOREFRONT_ROOT_DOMAIN)) externalReferrer = referrer.slice(0, 200);
    } catch {
        externalReferrer = '';
    }

    const session = safeStorage('sessionStorage');
    const alreadyCaptured = readJSON(session, CAMPAIGN_SESSION_KEY);

    // A new tagged link always wins; otherwise keep the first landing of this session.
    if (Object.keys(found).length === 0 && (alreadyCaptured || !externalReferrer)) return;

    writeJSON(session, CAMPAIGN_SESSION_KEY, {
        ...found,
        referrer: externalReferrer,
        landing_page: window.location.pathname.slice(0, 200),
        captured_at: Date.now(),
    });
}

function getCampaign() {
    const fromSession = readJSON(safeStorage('sessionStorage'), CAMPAIGN_SESSION_KEY);
    if (fromSession) return fromSession;
    const stored = readJSON(safeStorage('localStorage'), CAMPAIGN_STORED_KEY);
    if (stored && Date.now() - (stored.captured_at || 0) < CAMPAIGN_TTL_MS) return stored;
    return null;
}

// ─── Init (called once from src/index.js) ───────────────────────────────────

export function initAnalytics() {
    if (initialised || typeof window === 'undefined') return;
    initialised = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || gtag;

    gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        wait_for_update: 500,
    });
    gtag('set', 'ads_data_redaction', true);

    captureCampaign();

    const consent = getConsent();
    if (consent) {
        applyConsent(consent);
        syncShopifyConsent(consent);
    }
}

// ─── Page views ─────────────────────────────────────────────────────────────
// GA4 records route changes itself (Enhanced measurement → "Page changes based
// on browser history events"). Meta only records the first page, so we tell it
// about each route change.

export function trackPageView() {
    if (metaLoaded && hasConsent('marketing')) window.fbq('track', 'PageView');
}

// Every route change, including the first page, for our own analytics.
// liveTracker ignores it until the visitor has allowed Analytics cookies.
export function trackLivePageView(pathname) {
    safely('Live page view', () => livePageView(pathname));
}

function liveCartSummary(cart) {
    if (!cart) return undefined;
    return {
        items: cart.lines.reduce((sum, l) => sum + l.quantity, 0),
        value: cart.total,
        currency: cart.currency,
    };
}

// ─── Shopping events ────────────────────────────────────────────────────────

function gaItemFromProduct(product, quantity = 1) {
    return {
        item_id: shopifyNumericId(product.variantId) || shopifyNumericId(product.id),
        item_name: product.name,
        item_brand: BRAND,
        item_category: 'Perfume Oil',
        price: product.price,
        quantity,
    };
}

function gaItemFromLine(line) {
    return {
        item_id: shopifyNumericId(line.variantId),
        item_name: line.title,
        item_brand: BRAND,
        item_category: 'Perfume Oil',
        item_variant: line.variantTitle && line.variantTitle !== 'Default Title' ? line.variantTitle : undefined,
        price: line.price,
        quantity: line.quantity,
    };
}

// Product page view. `product` is the shape returned by getScent() in src/data/scents.js.
export function trackViewItem(product) {
    if (!product) return;
    const consent = getConsent();

    if (consent?.analytics) {
        gtag('event', 'view_item', {
            currency: product.currency,
            value: product.price,
            items: [gaItemFromProduct(product)],
        });
        safely('Live view_item', () => liveEvent('view_item', {
            product_handle: product.handle,
            value: product.price,
            currency: product.currency,
        }));
    }

    if (consent?.marketing) {
        const image = product.images?.[0]?.url || product.image || '';
        const url = productUrl(product.handle);
        const productId = shopifyNumericId(product.id);

        safely('Klaviyo Viewed Product', () => {
        const klaviyo = klaviyoQueue();
        klaviyo.push(['track', 'Viewed Product', {
            ProductName: product.name,
            ProductID: productId,
            Categories: ['Perfume Oil'],
            ImageURL: image,
            URL: url,
            Brand: BRAND,
            Price: product.price,
            Currency: product.currency,
        }]);
        klaviyo.push(['trackViewedItem', {
            Title: product.name,
            ItemId: productId,
            Categories: ['Perfume Oil'],
            ImageUrl: image,
            Url: url,
            Metadata: { Brand: BRAND, Price: product.price },
        }]);
        });

        if (metaLoaded) safely('Meta ViewContent', () => {
            window.fbq('track', 'ViewContent', {
                content_ids: [shopifyNumericId(product.variantId)],
                content_type: 'product',
                content_name: product.name,
                value: product.price,
                currency: product.currency,
            }, { eventID: eventId('view') });
        });
    }
}

// Successful add to cart. `cart` is the normalized cart returned after the add.
export function trackAddToCart(variantId, quantity, cart) {
    if (!cart) return;
    const line = cart.lines.find((l) => l.variantId === variantId);
    if (!line) return;
    const consent = getConsent();
    const value = Math.round(line.price * quantity * 100) / 100;

    if (consent?.analytics) {
        gtag('event', 'add_to_cart', {
            currency: line.currency,
            value,
            items: [{ ...gaItemFromLine(line), quantity }],
        });
        safely('Live add_to_cart', () => liveEvent('add_to_cart', {
            product_handle: line.handle,
            value,
            currency: line.currency,
            cart: liveCartSummary(cart),
        }));
    }

    if (consent?.marketing) {
        safely('Klaviyo Added to Cart', () => klaviyoQueue().push(['track', 'Added to Cart', {
            $value: cart.total,
            AddedItemProductName: line.title,
            AddedItemProductID: shopifyNumericId(line.productId),
            AddedItemVariantID: shopifyNumericId(line.variantId),
            AddedItemImageURL: line.image,
            AddedItemURL: productUrl(line.handle),
            AddedItemPrice: line.price,
            AddedItemQuantity: quantity,
            ItemNames: cart.lines.map((l) => l.title),
            CheckoutURL: cart.checkoutUrl,
            Currency: cart.currency,
            Items: cart.lines.map((l) => ({
                ProductID: shopifyNumericId(l.productId),
                VariantID: shopifyNumericId(l.variantId),
                ProductName: l.title,
                Quantity: l.quantity,
                ItemPrice: l.price,
                RowTotal: Math.round(l.price * l.quantity * 100) / 100,
                ProductURL: productUrl(l.handle),
                ImageURL: l.image,
            })),
        }]));

        if (metaLoaded) safely('Meta AddToCart', () => {
            window.fbq('track', 'AddToCart', {
                content_ids: [shopifyNumericId(line.variantId)],
                content_type: 'product',
                content_name: line.title,
                value,
                currency: line.currency,
            }, { eventID: eventId('atc') });
        });
    }
}

export function trackViewCart(cart) {
    if (!cart || cart.lines.length === 0 || !hasConsent('analytics')) return;
    gtag('event', 'view_cart', {
        currency: cart.currency,
        value: cart.total,
        items: cart.lines.map(gaItemFromLine),
    });
    safely('Live view_cart', () => liveEvent('view_cart', {
        value: cart.total,
        currency: cart.currency,
        cart: liveCartSummary(cart),
    }));
}

export function trackBeginCheckout(cart) {
    if (!cart || cart.lines.length === 0) return;
    const consent = getConsent();

    if (consent?.analytics) {
        gtag('event', 'begin_checkout', {
            currency: cart.currency,
            value: cart.total,
            items: cart.lines.map(gaItemFromLine),
        });
        safely('Live begin_checkout', () => liveEvent('begin_checkout', {
            value: cart.total,
            currency: cart.currency,
            cart: liveCartSummary(cart),
        }));
    }

    if (consent?.marketing && metaLoaded) safely('Meta InitiateCheckout', () => {
        window.fbq('track', 'InitiateCheckout', {
            content_ids: cart.lines.map((l) => shopifyNumericId(l.variantId)),
            content_type: 'product',
            num_items: cart.lines.reduce((sum, l) => sum + l.quantity, 0),
            value: cart.total,
            currency: cart.currency,
        }, { eventID: eventId('ic') });
    });
}

// Ties the browser to a Klaviyo profile (e.g. after the email popup), so
// Viewed Product / Added to Cart reach the right person for abandonment flows.
export function identifyEmail(email) {
    if (!email || !hasConsent('marketing')) return;
    klaviyoQueue().push(['identify', { $email: email }]);
}

// ─── Scent quiz ─────────────────────────────────────────────────────────────

// Records the visitor's quiz result (scent profile, roadmap 2.1) so customers
// can be grouped by it: a GA4 event, a Klaviyo event plus profile property for
// email segments, and a copy kept in this browser that's added to the Shopify
// order at checkout. Each part only runs with the matching consent.
export function trackQuizResult({ profile, bestMatch, alternativeMatch, bestHandle, alternativeHandle }) {
    if (!profile) return;
    const consent = getConsent();

    if (consent?.analytics || consent?.marketing) {
        writeJSON(safeStorage('localStorage'), QUIZ_RESULT_KEY, { profile, bestMatch, bestHandle, alternativeHandle });
    }

    if (consent?.analytics) safely('GA4 quiz_complete', () => {
        gtag('event', 'quiz_complete', {
            scent_profile: profile,
            best_match: bestMatch,
            alternative_match: alternativeMatch,
        });
    });

    if (consent?.analytics) safely('Live quiz_complete', () => liveEvent('quiz_complete', {
        product_handle: bestHandle,
        props: { profile, best_match: bestMatch },
    }));

    if (consent?.marketing) safely('Klaviyo Completed Scent Quiz', () => {
        const klaviyo = klaviyoQueue();
        klaviyo.push(['track', 'Completed Scent Quiz', {
            'Scent Profile': profile,
            'Best Match': bestMatch,
            'Alternative Match': alternativeMatch,
        }]);
        // Only sticks to visitors Klaviyo already knows (e.g. via the email
        // popup or an email click) — anonymous visitors have no profile yet.
        klaviyo.push(['identify', {
            'Scent Profile': profile,
            'Quiz Best Match': bestMatch,
        }]);
    });
}

// The last quiz result saved by trackQuizResult, or null (no quiz taken, or
// no consent to keep it). Used by the cart to recommend the quiz matches.
export function getQuizResult() {
    return readJSON(safeStorage('localStorage'), QUIZ_RESULT_KEY);
}

// ─── Checkout hand-over ─────────────────────────────────────────────────────

function gaGet(field) {
    return new Promise((resolve) => {
        if (!gaLoaded || !hasConsent('analytics') || typeof window.gtag !== 'function') {
            resolve('');
            return;
        }
        const timer = setTimeout(() => resolve(''), 400);
        try {
            window.gtag('get', GA_ID, field, (value) => {
                clearTimeout(timer);
                resolve(value ? String(value) : '');
            });
        } catch {
            clearTimeout(timer);
            resolve('');
        }
    });
}

// Cart attributes saved onto the Shopify order. Keys start with "_" so they're
// hidden from the shopper at checkout but visible on the order in Shopify admin.
export async function getCheckoutAttributes() {
    const consent = getConsent() || { analytics: false, marketing: false };
    const campaign = getCampaign() || {};
    const attributes = [];
    const add = (key, value) => {
        if (value !== undefined && value !== null && value !== '') {
            attributes.push({ key, value: String(value).slice(0, 255) });
        }
    };

    CAMPAIGN_PARAMS.forEach((key) => add(`_${key}`, campaign[key]));
    add('_referrer', campaign.referrer);
    add('_landing_page', campaign.landing_page);
    add('_consent', `analytics=${consent.analytics ? 'yes' : 'no'};marketing=${consent.marketing ? 'yes' : 'no'}`);

    if (consent.analytics || consent.marketing) {
        const quiz = readJSON(safeStorage('localStorage'), QUIZ_RESULT_KEY);
        add('_scent_profile', quiz?.profile);
        add('_quiz_best_match', quiz?.bestMatch);
    }

    if (consent.analytics) {
        const [clientId, sessionId] = await Promise.all([gaGet('client_id'), gaGet('session_id')]);
        add('_ga_client_id', clientId);
        add('_ga_session_id', sessionId);
        // Empty (and so skipped) unless our own analytics is switched on.
        add('_md_session_id', liveSessionId());
    }

    return attributes;
}

// Adds the UTM tags to the checkout link so Shopify's own reports
// (Analytics → Sessions / Orders by referrer) credit the right campaign.
export function decorateCheckoutUrl(url) {
    const campaign = getCampaign();
    if (!url || !campaign) return url;
    try {
        const target = new URL(url);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
            if (campaign[key] && !target.searchParams.has(key)) target.searchParams.set(key, campaign[key]);
        });
        return target.toString();
    } catch {
        return url;
    }
}
